import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { addCommentOrReport, attachFile, deleteCommentOrReport, updateDescription, updateReportStatusTC, updateSubject, uploadCommentOrReport } from '../../../../store/details/comment-or-report/actions';

import { CommentOrReportItem } from './comment-or-report-item';
import { CommentOrReport as CommentOrReportType } from '../../../../store/details/comment-or-report/model';
import { CommunicationOverviewDocumentItem } from '../../../../store/details/overview/types';
import { Dispatch } from 'redux';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import React from 'react';
import { ReportStatusTC } from '../../../../business/models/typecode.enums';
import { RimTotal } from '../../../../store/details/rim/types';
import { SvsItem } from '../../../../store/details/svs/types';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { connect } from 'react-redux';
import { getDownloadLink } from '../../../../store/shared/actions';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import styles from './comment-or-report.module.css';
import { SelectChangeEvent } from '@mui/material';

interface PropsFromState {
  readonly commentsOrReports: CommentOrReportType[];
  readonly documentTypeTCValues: TypeCodeTranslation[];
  readonly uiDisabled: boolean;
  readonly sentReports: CommunicationOverviewDocumentItem[];
  readonly sentFreeTexts: CommunicationOverviewDocumentItem[];
  readonly svsList: SvsItem[];
  readonly rimList: RimTotal[];
  readonly caseBlocked: boolean;
  readonly caseCode: string;
  readonly instructionId: number;
  readonly currentLanguage: string;
}

interface PropsFromDispatch {
  readonly uploadCommentOrReport: typeof uploadCommentOrReport;
  readonly deleteCommentOrReport: typeof deleteCommentOrReport;
  readonly addCommentOrReport: typeof addCommentOrReport;
  readonly updateReportStatusTC: typeof updateReportStatusTC;
  readonly attachFile: typeof attachFile;
  readonly updateDescription: typeof updateDescription;
  readonly updateSubject: typeof updateSubject;
  readonly getDownloadLink: typeof getDownloadLink;
}

interface Props {
  readonly handleDownloadClick: (amazonKey: string, filename: string) => void; // Handle independently
}

type AllProps = PropsFromDispatch & PropsFromState & ConnectedReduxProps & Props;

class CommentOrReport extends React.Component<AllProps> {
  private localization: Ii18n;

  constructor(props: AllProps) {
    super(props);
    this.localization = getI18n();
  }

  private onDeleteItem(index: number) {
    if (this.props.caseBlocked) { return; }
    this.props.deleteCommentOrReport(this.props.caseCode, index);
  }

  private onUploadItem(index: number) {
    if (this.props.caseBlocked) { return; }
    this.props.uploadCommentOrReport(this.props.instructionId, index);
  }

  private addCommentOrReport() {
    if (this.props.caseBlocked) { return; }
    this.props.addCommentOrReport();
  }

  private onReportStatusTCChanged(index: number, value: string) {
    if (this.props.caseBlocked) { return; }
    this.props.updateReportStatusTC(index, value);
    if (this.props.commentsOrReports[index].reportStatusTC !== ReportStatusTC.FreeText) {
      // clear description and dispatch
      this.props.updateDescription(index, '');
    }
  }

  private onDescriptionChanged(index: number, value: string) {
    if (this.props.caseBlocked) { return; }
    this.props.updateDescription(index, value);
  }

  private onSubjectChanged(index: number, value: string) {
    if (this.props.caseBlocked) { return; }
    this.props.updateSubject(index, value);
  }

  isDisabled = (item: CommentOrReportType) => {
    if (this.props.caseBlocked) { return true; }
    if (this.props.sentReports.filter(report => report.id === item.id).length > 0 || this.props.sentFreeTexts.filter(txt => txt.id === item.id).length > 0) { return true; }
    return false;
  }

  private endReportWithoutRimOrSvs = () => {
    if (this.props.commentsOrReports.filter(x => x.reportStatusTC === ReportStatusTC.End).length > 0 && this.props.svsList.length === 0 && this.props.rimList.length === 0) {
      return true;
    }
    return false;
  }

  get commentsOrReports() {
    return this.props.commentsOrReports.map((x, i) => {
      return (
        <div className={styles.item} key={i}>
          <CommentOrReportItem
            disabled={this.isDisabled(x)}
            filename={x.filename}
            url={x.url}
            amazonKey={x.amazonKey}
            content={x.content}
            description={x.description}
            subjectLine={x.subjectLine}
            isOnline={x.isOnline}
            reportStatusTC={x.reportStatusTC}
            reportStatusTCValues={this.props.documentTypeTCValues}
            currentLanguage={this.props.currentLanguage}
            onRemoveClick={() => this.onDeleteItem(i)}
            onUploadClick={() => this.onUploadItem(i)}
            onDocumentTypeChanged={(event: SelectChangeEvent<string>) => this.onReportStatusTCChanged(i, event.target.value)}
            onAttachClick={(attachment: CommentOrReportType) => this.props.attachFile(i, attachment)}
            onDescriptionChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => this.onDescriptionChanged(i, event.target.value)}
            onSubjectChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => this.onSubjectChanged(i, event.target.value)}
            handleDownloadClick={this.props.handleDownloadClick}
            VirusDetected={x.VirusDetected}
            AVScanned={x.AVScanned}
          />
        </div>
      );
    });
  }

  render() {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          {this.endReportWithoutRimOrSvs() &&
            <div className={styles.itemsContent}>
              <div className={styles.item}>
                <div className={styles.warning}>
                  {this.localization.get('details.commentOrReport.endReportWithoutRimOrSvs')}
                </div>
              </div>
            </div>
          }
          <div className={styles.itemsContent}>
            {this.commentsOrReports}
          </div>
        </div>
        <div className={styles.actionsColumn}>
          <div className={styles.add} onClick={() => this.addCommentOrReport()}>
            <img alt="" className={styles.img} src={require('../../../../assets/images/add_85x85.png')} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ details: { detailsCommentOrReport, detailsOverview, detailsRim, detailsSvs }, shared, caseState: { selectedCase } }: ApplicationState): PropsFromState => ({
  commentsOrReports: detailsCommentOrReport.commentsOrReports,
  documentTypeTCValues: detailsCommentOrReport.documentTypeTCValues,
  uiDisabled: shared.isUIDisabled,
  sentReports: detailsOverview.reportList,
  sentFreeTexts: detailsOverview.freeTextList,
  svsList: detailsSvs.svsItems,
  rimList: detailsRim.rimsTotal,
  caseBlocked: selectedCase.blocked,
  caseCode: selectedCase.caseCode,
  instructionId: selectedCase.instructionId,
  currentLanguage: shared.currentLanguage,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  uploadCommentOrReport: (instructionId: number, index: number) => dispatch(uploadCommentOrReport(instructionId, index)),
  deleteCommentOrReport: (caseCode: string, index: number) => dispatch(deleteCommentOrReport(caseCode, index)),
  addCommentOrReport: () => dispatch(addCommentOrReport()),
  updateReportStatusTC: (index: number, value: string) => dispatch(updateReportStatusTC(index, value)),
  attachFile: (index: number, attachment: CommentOrReportType) => dispatch(attachFile(index, attachment)),
  updateDescription: (index: number, descrition: string) => dispatch(updateDescription(index, descrition)),
  updateSubject: (index: number, subject: string) => dispatch(updateSubject(index, subject)),
  getDownloadLink: (amazonKey: string, url: string, content: string, filename: string, onClickLink) => dispatch(getDownloadLink(amazonKey, url, content, filename, onClickLink))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CommentOrReport);
